import Modal from "@components/common/Modal";
import Button from "@components/common/Button";
import Carousel, { type CarouselHandle } from "../common/Carousel/Carousel";
import React, { useRef, useState, type RefObject } from "react";
import { ONE_DAY_MS, ONE_YEAR_MS } from "./consts";
import Icon from "../common/Icon";

type SlideProps = {
	title: string;
	description: string;
	video: string;
};

function Slide({ title, description, video }: SlideProps) {
	return (
		<div className="grid h-full min-h-0 grid-rows-[minmax(1fr,2fr),_auto] gap-4 overflow-y-scroll">
			<div className="flex min-h-[160px] min-w-0 items-start justify-center overflow-hidden sm:min-h-[200px]">
				<video
					className="block max-h-full w-auto max-w-full object-contain"
					controls={false}
					autoPlay
					muted
					loop
					playsInline
					preload="auto"
				>
					<source src={video + ".mp4"} type="video/mp4" />
					<source src={video + ".webm"} type="video/webm" />
					Your browser does not support the video tag.
				</video>
			</div>
			<div className="flex flex-col gap-4 p-4 sm:p-8 pt-0">
				<h3 className="text-lg font-sans font-semibold">{title}</h3>
				<p>{description}</p>
			</div>
		</div>
	);
}

function NewFeaturesModal({
	isOpen,
	onClose,
	triggerRef,
}: {
	isOpen: boolean;
	onClose: (duration: number) => void;
	triggerRef?: RefObject<HTMLElement | null>;
}) {
	const [carouselCurrent, setCarouselCurrent] = useState(0);
	const [carouselCount, setCarouselCount] = useState(0);
	const carouselRef = useRef<CarouselHandle | null>(null);

	const _onClose = () => {
		onClose(ONE_YEAR_MS);
		carouselCurrent && setCarouselCurrent(0);
	};

	const remindMe = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		onClose(ONE_DAY_MS);
		carouselCurrent && setCarouselCurrent(0);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={_onClose}
			closeVariant="primary"
			closeElement={
				<>
					Close
					<Icon name="cyber-close" size="sm" />
				</>
			}
			animateCloseToTarget={Boolean(triggerRef)}
			closeAnimationTargetRef={triggerRef || null}
			header={<h2>New features</h2>}
			mainPadding="p-0 pb-8"
			minW=""
			formClass="h-[80vh]"
			main={
				<div className="flex h-full min-h-0 max-h-full flex-col">
					<Carousel
						ref={carouselRef}
						className="flex-1"
						onChange={({ current, count }) => {
							setCarouselCurrent(current);
							setCarouselCount(count);
						}}
						slides={[
							<Slide
								key="slide1"
								title="1. A smarter, faster and easier portal experience"
								description="We've upgraded the portal based on your feedback, with new features and smarter improvements that make it faster and easier to find the information and policies you need."
								video="/video/1_First"
							/>,
							<Slide
								key="slide2"
								title="2. Advanced date search"
								description="Both the Policies and Invoice tabs have been upgraded. Use advanced date ranges, status and interest filters, or search by vessel, organisation, IMO or policy number to quickly find the documents you need."
								video="/video/2_Filter"
							/>,
							<Slide
								key="slide3"
								title="3. Bulk downloads"
								description="Once you've found the policies you need, you can easily download all related documents. New uploads are available anytime through the notification centre."
								video="/video/3_BulkDownload"
							/>,
							<Slide
								key="slide4"
								title="4. Export to CSV"
								description="Fleet information and vessel relations can now be exported as a CSV file, making it easier to control your insurance data or work with reports outside the portal."
								video="/video/4_ExportCSV"
							/>,
							<Slide
								key="slide5"
								title="5. Updated permissions"
								description="We've introduced a more flexible and detailed permission system. Most users won't notice a change, but if something you expect to see is missing, please let us know and we'll help you resolve it."
								video="/video/5_Permissions"
							/>,
						]}
					/>
				</div>
			}
			footer={
				<div className="w-full flex flex-row justify-between gap-6">
					<div className="flex-1 hidden sm:block" />
					<div className="flex items-center justify-center gap-4">
						<Button
							onClick={() => carouselRef.current?.prev()}
							disabled={carouselCurrent === 0}
						>
							Previous
						</Button>
						<Button
							onClick={() => carouselRef.current?.next()}
							disabled={
								carouselCount > 0 &&
								carouselCurrent === carouselCount - 1
							}
						>
							Next
						</Button>
					</div>
					<div className="flex-1 flex justify-center sm:justify-end">
						<Button onClick={remindMe} outline>
							Show me later
						</Button>
					</div>
				</div>
			}
		/>
	);
}

export default NewFeaturesModal;
